import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { parseDate, nowISOString, dateRangeOverlap } from '../common/utils/dateUtils';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareWeather = (w1, w2) => w1.timestamp === w2.timestamp;

const weatherSlice = createSlice({
    name: 'weather',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = [action.payload]
        },
        addOrUpdate: (state, action) => {
            for (const w of ensureArray(action.payload)) {
                let idx = state.data.findIndex(w2 => compareWeather(w, w2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, w)
                } else {
                    state.data.push(w);
                }
            }
        },
        remove: (state, action) => {
            for (const w of action.payload) {
                let idx = state.data.findIndex(w2 => compareWeather(w2, w));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => weatherSlice.getInitialState());
    }
});

// #region network operations
const fetchCurrentWeather = network => network.get('/api/v1/weather/' + encodeURIComponent(nowISOString()), weatherSlice.actions.addOrUpdate);
const fetchWeatherInRange = (start, end) => network => network.get('/api/v1/weatherInRange/' + encodeURIComponent(start) + '/' + encodeURIComponent(end), weatherSlice.actions.addOrUpdate )
export const fetchWeather = timestamp => network => network.get('/api/v1/weather/' + encodeURIComponent(timestamp ?? nowISOString()), weatherSlice.actions.addOrUpdate);
// #endregion

// #region selectors
const selectAllWeather = state => state.weather.data;

const selectCurrentWeather = state => state.weather.data.reduce((v, w) => !v || (w && parseDate(w.timestamp) > parseDate(v.timestamp)) ? w : v, null);

const selectWeatherInRange = (start, end) => createSelector([selectAllWeather], weather => {
    let result = Object.values(weather).filter(s => dateRangeOverlap(s, { start, end }));
    result.sort((a, b) => (parseDate(a.start)) - (parseDate(b.start)));
    return result;
});


const selectWeatherByTimestamp = createSelector([selectAllWeather, (weather, timestamp) => timestamp], (weather, timestamp) => {
    const ct = timestamp ? parseDate(timestamp) : new Date();
    return weather.filter(w => parseDate(w.timestamp) < ct).reduce((best, cur) => {
        if (!best) {
            return cur;
        }
        if ((ct - parseDate(best.timestamp)) > (ct - parseDate(cur.timestamp))) {
            return cur;
        }
        return best;
    }, null);
});
const selectWeather = timestamp => state => selectWeatherByTimestamp(state, timestamp);
// #endregion

// #region listeners
const listen = {
    weatherUpdated: (weather, {dispatch }) => dispatch(weatherSlice.actions.addOrUpdate(weather))
}

export const [useCurrentWeather, realizeCurrentWeather] = dataHookFactory(selectCurrentWeather, { run: fetchCurrentWeather, listen });
export const [useWeather,realizeWeather] = dataHookFactory(selectWeather, initMapFactory(fetchWeather, listen));
export const [useAllWeather] = dataHookFactory(selectAllWeather, { run: fetchWeather, listen });
export const [useWeatherInRange, realizeWeatherInRange] = dataHookFactory(selectWeatherInRange, initMapFactory(fetchWeatherInRange, listen))

const reducerDetails = { name: weatherSlice.name, reducer: weatherSlice.reducer };
export default reducerDetails;